import { useCallback, useEffect, useRef, useState } from "react";

import {
  IngestJob,
  MAX_CONCURRENT_JOBS,
  activeJobCount,
  labelForBatch,
  loadJobs,
  saveJobs,
} from "./ingestJobs";
import { IngestReviewItem } from "./types";
import {
  CatalogApi,
  billedTokensOf,
  extractChainProgress,
  extractIngestResult,
} from "./useCatalogApi";

/** Thrown by `submit` when too many jobs are already in flight. */
export class IngestJobLimitError extends Error {
  constructor() {
    super(
      `You already have ${MAX_CONCURRENT_JOBS} research runs going. Wait for one to finish (or clear it) before starting another.`
    );
    this.name = "IngestJobLimitError";
  }
}

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Background job manager for ingest research runs. Jobs run independently of any
 * open dialog, persist to localStorage (see ingestJobs.ts), survive tab reloads
 * (polling resumes on mount for any unfinished run), and are capped at
 * MAX_CONCURRENT_JOBS in flight. Cancelling stops client-side tracking/polling
 * (there is no server-side chain cancel endpoint today).
 */
export const useIngestJobs = (api: CatalogApi) => {
  const [jobs, setJobs] = useState<IngestJob[]>(() => loadJobs());
  const jobsRef = useRef<IngestJob[]>(jobs);
  jobsRef.current = jobs;

  // Per-job poller handles so we can cancel a run's polling loop.
  const pollers = useRef<Map<string, { cancelled: boolean }>>(new Map());

  // Central mutation: compute next list, persist, and set state.
  const mutate = useCallback((fn: (prev: IngestJob[]) => IngestJob[]) => {
    setJobs((prev) => {
      const next = fn(prev);
      jobsRef.current = next;
      saveJobs(next);
      return next;
    });
  }, []);

  const patchJob = useCallback(
    (id: string, patch: Partial<IngestJob>) =>
      mutate((prev) =>
        prev.map((j) =>
          j.id === id ? { ...j, ...patch, updatedAt: Date.now() } : j
        )
      ),
    [mutate]
  );

  // Poll one execution to completion, updating its job as it goes.
  const poll = useCallback(
    async (jobId: string, executionId: string) => {
      const handle = { cancelled: false };
      pollers.current.set(jobId, handle);
      const deadline = Date.now() + POLL_TIMEOUT_MS;

      try {
        while (!handle.cancelled && Date.now() < deadline) {
          await sleep(POLL_INTERVAL_MS);
          if (handle.cancelled) return;
          let resp;
          try {
            resp = await api.getChainExecution(executionId);
          } catch {
            // Transient network/status error — keep trying until the deadline.
            continue;
          }
          if (handle.cancelled) return;

          if (resp.status === "completed") {
            const result = extractIngestResult(resp);
            patchJob(jobId, {
              status: "completed",
              progress: extractChainProgress(resp),
              credits: billedTokensOf(resp),
              resultItems: result.items,
              resultCount: result.items.length,
              summary: result.summary,
              excluded: result.excluded,
            });
            return;
          }
          if (resp.status === "failed") {
            patchJob(jobId, {
              status: "failed",
              progress: extractChainProgress(resp),
              credits: billedTokensOf(resp),
              error: resp.error || "Research run failed.",
            });
            return;
          }
          patchJob(jobId, {
            status: "running",
            progress: extractChainProgress(resp),
            credits: billedTokensOf(resp),
          });
        }
        if (!handle.cancelled) {
          patchJob(jobId, {
            status: "failed",
            error: "Research run timed out.",
          });
        }
      } finally {
        pollers.current.delete(jobId);
      }
    },
    [api, patchJob]
  );

  // Resume polling any unfinished jobs left over from a previous session/tab.
  useEffect(() => {
    jobsRef.current.forEach((j) => {
      const inFlight = j.status === "starting" || j.status === "running";
      if (inFlight && j.executionId && !pollers.current.has(j.id)) {
        void poll(j.id, j.executionId);
      }
    });
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep multiple open tabs roughly in sync: when another tab writes jobs,
  // reload them here (this tab keeps polling only the runs it started).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key.startsWith("catalogManager.ingestJobs")) {
        const fresh = loadJobs();
        jobsRef.current = fresh;
        setJobs(fresh);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /**
   * Start a new research run in the background. Returns the new job id. Throws
   * IngestJobLimitError if MAX_CONCURRENT_JOBS are already in flight.
   */
  const submit = useCallback(
    async (
      chainId: string,
      foodInput: string,
      submittedCount: number
    ): Promise<string> => {
      if (activeJobCount(jobsRef.current) >= MAX_CONCURRENT_JOBS) {
        throw new IngestJobLimitError();
      }
      const started = await api.startChain(
        chainId,
        { food_input: foodInput },
        { metadata: { feature: "ingest-batch" } }
      );
      const execId = started.id;
      if (!execId) throw new Error("Chain did not return an execution id.");

      const now = Date.now();
      const job: IngestJob = {
        id: execId,
        executionId: execId,
        chainId,
        status: "running",
        label: labelForBatch(foodInput, submittedCount),
        submittedCount,
        createdAt: now,
        updatedAt: now,
        credits: billedTokensOf(started) ?? null,
        progress: extractChainProgress(started),
      };
      mutate((prev) => [job, ...prev]);
      void poll(job.id, execId);
      return job.id;
    },
    [api, mutate, poll]
  );

  /**
   * Re-run the ingest chain for a SINGLE review item (from a review row) as a
   * background job. Doesn't count against the batch limit and isn't shown in the
   * batch tray. Its one result is picked up by the review UI (via the retry job
   * it returns) and swapped in for the originating item. Returns the job id.
   */
  const retryItem = useCallback(
    async (
      chainId: string,
      foodInput: string,
      label: string,
      originExecutionId: string,
      itemIndex: number
    ): Promise<string> => {
      const started = await api.startChain(
        chainId,
        { food_input: foodInput },
        { metadata: { feature: "ingest-retry" } }
      );
      const execId = started.id;
      if (!execId) throw new Error("Retry did not return an execution id.");

      const now = Date.now();
      const job: IngestJob = {
        id: execId,
        executionId: execId,
        chainId,
        status: "running",
        kind: "retry",
        retryForExecutionId: originExecutionId,
        retryItemIndex: itemIndex,
        label,
        submittedCount: 1,
        createdAt: now,
        updatedAt: now,
        credits: billedTokensOf(started) ?? null,
        progress: extractChainProgress(started),
      };
      mutate((prev) => [job, ...prev]);
      void poll(job.id, execId);
      return job.id;
    },
    [api, mutate, poll]
  );

  /**
   * Replace one item (by index) in a run's stored resultItems, so a retried item
   * is persisted on the origin run and survives a reload / re-open from the tray.
   */
  const replaceRunItem = useCallback(
    (executionId: string, index: number, newItem: IngestReviewItem) => {
      mutate((prev) =>
        prev.map((j) => {
          if (
            j.executionId !== executionId ||
            !Array.isArray(j.resultItems) ||
            index < 0 ||
            index >= j.resultItems.length
          ) {
            return j;
          }
          const items = j.resultItems.slice();
          items[index] = newItem;
          return { ...j, resultItems: items, updatedAt: Date.now() };
        })
      );
    },
    [mutate]
  );

  // Stop tracking/polling a run. (No server-side cancel; the chain may still
  // finish server-side, but we stop watching and mark it cancelled locally.)
  const cancel = useCallback(
    (jobId: string) => {
      const handle = pollers.current.get(jobId);
      if (handle) handle.cancelled = true;
      patchJob(jobId, { status: "cancelled" });
    },
    [patchJob]
  );

  // Remove a single finished job from the list/storage.
  const clear = useCallback(
    (jobId: string) => {
      const handle = pollers.current.get(jobId);
      if (handle) handle.cancelled = true;
      mutate((prev) => prev.filter((j) => j.id !== jobId));
    },
    [mutate]
  );

  // Remove all finished (terminal) jobs, leaving in-flight ones.
  const clearFinished = useCallback(() => {
    mutate((prev) =>
      prev.filter((j) => j.status === "starting" || j.status === "running")
    );
  }, [mutate]);

  const getJob = useCallback(
    (id: string): IngestJob | undefined =>
      jobsRef.current.find((j) => j.id === id),
    []
  );

  // Record review-item keys written to the catalog so the run's stored history
  // reflects progress (e.g. "written 3/8") and survives a tab reload.
  const recordWrites = useCallback(
    (jobId: string, keys: string[]) => {
      if (keys.length === 0) return;
      mutate((prev) =>
        prev.map((j) => {
          if (j.id !== jobId) return j;
          const merged = Array.from(
            new Set([...(j.writtenKeys || []), ...keys])
          );
          return {
            ...j,
            writtenKeys: merged,
            writtenCount: merged.length,
            updatedAt: Date.now(),
          };
        })
      );
    },
    [mutate]
  );

  return {
    jobs,
    submit,
    retryItem,
    replaceRunItem,
    cancel,
    clear,
    clearFinished,
    getJob,
    recordWrites,
    activeCount: activeJobCount(jobs),
  };
};

export type IngestJobsApi = ReturnType<typeof useIngestJobs>;
